import { immerable } from "immer"
import { nullSkeleton, Skeleton } from "./Mannequin.tsx"
import { Quat } from "./VectorMath.ts"
import { Decoder } from "./Coding.ts"

/**
 * A `Pose` stores, for every `Bone` in a `Skeleton`, additional rotation to apply to the `Bone` on top of the `Bone`'s bind pose (which is represented by the `Bone`'s `worldFromModel` matrix.)
 */
export class Pose {
  readonly [immerable] = true

  /**
   * Initialize myself with the identity rotation for every `Bone` in `Skeleton`.
   */
  constructor(
    readonly bonePoses: Quat[]
  ) { }

  static forSkeleton(skeleton: Skeleton): Pose {
    return new Pose(skeleton.bones.map(() => Quat.identity))
  }

  static decodeFrom(decoder: Decoder): Pose {
    const container = decoder.keyedDecoder()
    return new Pose(
      container.decodeArray('bonePoses', Quat.decodeFrom)
    )
  }

  isCompatibleWith(skeleton: Skeleton): boolean {
    if (skeleton === nullSkeleton) {
      return true
    }
    return this.bonePoses.length === skeleton.bones.length
  }

  static interpolate(pose0: Pose, pose1: Pose, t: number): Pose {
    return new Pose(
      pose0.bonePoses.map((q0, i) => {
        const q1 = pose1.bonePoses[i]
        return Quat.slerp(q0, q1, t)
      })
    )
  }
}
